/**
 * Types for public/data/*.json, emitted by tools/export_data.py from the Python source
 * (/Volumes/Lab/CardKnight3D, read-only). Hand-written to match the exporter; the
 * tests in tests/data-*.test.ts check the invariants. See docs/DATA_PIPELINE.md.
 *
 * Conventions: Python tuples are arrays, sets are sorted arrays, int dict keys are
 * strings. Field names inside verbatim Python tables keep their Python spelling
 * (snake_case); exporter-added envelope fields are camelCase.
 */

export type Tile = [number, number];
export type FlagName = string;
/** "npc:<map>:<index>" | "enemy:<map>:<index>:pre|post" | "fn:map_defs.<name>" */
export type DialogueRef = string;

export interface SourceMeta { source: string; sourceHead: string | null; note: string }
interface Envelope { version: number; meta: SourceMeta }

// ───────────────────────────────────────────────────────────── maps.json

export interface MapExit {
  tile: Tile; dest: string; spawn: string;
  req_flag?: FlagName | null;
  /** req_flag is waived while this flag is UNSET */
  req_unless?: FlagName;
}

export interface Gated { require?: FlagName[]; forbid?: FlagName[] }

export interface OnTalkBattle {
  flag: FlagName; encounter?: string; count?: number; talk_only?: boolean;
  radius?: number; req?: FlagName; note?: string;
}

export interface MapNpc extends Gated {
  tile: Tile;
  name: string;
  color: [number, number, number];
  /** reference into dialogue.json callables */
  dialog: DialogueRef;
  /** "<map>:<name>:<authored col>,<authored row>" — keys per-instance voice + editor turns */
  _inst_id: string;
  /** index in map_defs.MAPS[map].npcs (dialogue ref uses it) */
  _mdef_index: number;
  wander?: number;
  facing?: 'sw' | 'se' | 'nw' | 'ne';
  id?: string;
  on_talk_battle?: OnTalkBattle;
  on_approach?: FlagName;
  gate?: { require: FlagName[]; until: FlagName; tile: Tile; tip_flag: FlagName };
  ghost?: boolean;
  ghost_fight?: { encounter: string };
  raise_card?: boolean;
  portrait?: string;
}

export interface MapEnemy extends Gated {
  waypoints: Tile[];
  speed?: number;
  interval?: number;
  encounter?: string;
  kind?: string;
  win_flag?: FlagName;
  on_defeat_flag?: FlagName;
  trigger?: boolean;
  chase?: string;
  note?: string;
  speaker?: string;
  story_trigger?: boolean;
  pre_dialog?: DialogueRef;
  post_dialog?: DialogueRef;
}

/** Where an object came from; decides the load-time gate behaviour (see DATA_PIPELINE.md). */
export type ObjectSrc = 'override' | 'map_defs' | 'map_defs_gated' | 'chest';

export interface MapObject extends Gated {
  tile: Tile; asset: string; _src: ObjectSrc;
  flip?: boolean; _veg?: boolean; pole_b?: unknown; depth?: string; hide_flag?: FlagName;
}

/** Dialogue page inside a stage: bare string (narration) or [speaker, text, emote?]. */
export type StageLine = string | [string, string] | [string, string, string];

export interface Stage extends Gated {
  lines?: StageLine[];
  set_flag?: FlagName; set_flags?: FlagName[]; clear_flags?: FlagName[];
  toast?: string; raise_card?: boolean;
  give_key_item?: string; give_pips?: number; give_item?: string;
  give_card?: string; give_card_n?: number; give_armor?: string; give_cardstock?: string;
  take_key_items?: string[]; take_key_group?: [string, number];
  ambush?: { encounter?: string; collect?: string };
  sfx?: string;
  confirm?: { title: string; msg: string; yes?: string; no?: string };
  warp?: [string, string];
  deck_case?: boolean;
  [k: string]: unknown;
}

export interface Interactable {
  tile: Tile | [number, number]; stages: Stage[]; id?: string; reticle?: boolean;
  dynamic?: 'socket';
}

export interface MapDef {
  name: string;
  /** which merge produced this entry: '3d' = ck3d/mapdata.load, '2d' = Overworld.load_map */
  runtime: '3d' | '2d';
  scroll: boolean;
  dense: boolean;
  grid: number[][];
  heights: number[][] | null;
  water_depth: number | null;
  tile_w: number; tile_h: number; wall_h: number;
  iso_ratio: number | null;
  /** stale map_defs value; runtime music comes from encounters.json mapMusic */
  music: string | null;
  path_mat: string | null;
  spawns: Record<string, Tile>;
  npcs: MapNpc[];
  enemies: MapEnemy[];
  exits: MapExit[];
  props: Array<Gated & { tile: Tile; type: string }>;
  objects: MapObject[];
  buildings: Array<{ sprite: string; origin: Tile; w: number; h: number; scale?: number; flip?: boolean }>;
  _chests: Array<{ tile: Tile; give: { kind: string; value: unknown } }>;
  critters: Array<{ tile: Tile; kind: string }>;
  interactables: Interactable[];
  blocking_msg: Record<string, string>;
  hazards: Array<Record<string, unknown>>;
  warps: Array<[Tile, Tile]>;
  blocked: Tile[];
  walkable: Tile[];
  poly_mode: boolean;
  poly_blocked: Array<Array<[number, number]>>;
  poly_occluders: Array<Array<[number, number]>>;
  /** 3D merge drops fx (null); 2D carries the raw editor list */
  fx: Array<Record<string, unknown>> | null;
  hazard_zones?: Array<Record<string, unknown>>;
  cart_tracks?: Record<string, Array<[number, number]>>;
  gate?: unknown;
  puzzle?: unknown;
}

export interface MapsFile extends Envelope {
  count: number;
  maps: Record<string, MapDef>;
  tileCodes: Record<'GRASS' | 'WALL' | 'WATER' | 'PATH' | 'DIRT', number>;
  transforms: Record<string, number>;
  transformExamples: Record<string, string[]>;
  mergeRule: Record<'3d' | '2d', string>;
}

/** map_defs_raw.json: map_defs.MAPS after import mutations, before overrides (loose). */
export interface MapDefsRawFile extends Envelope { note: string; maps: Record<string, Record<string, unknown>> }

export interface MergeReportFile extends Envelope {
  note: string;
  diffs: Record<string, { emitted: '3d' | '2d'; fields: Record<string, string> }>;
}

// ───────────────────────────────────────────────────────────── dialogue.json

export type DialogueLine =
  | { speaker: 'npc'; text: string; deleted?: boolean }
  | { speaker: 'oden'; text: string; emo: string | null; deleted?: boolean }
  | { speaker: 'named'; name: string; text: string; mood?: string; deleted?: boolean };

export interface Condition { kind: 'get' | 'req'; flag: FlagName; value: boolean }

export interface DialogueBranch { conditions: Condition[]; lines: DialogueLine[] }

export interface DialogueOwner {
  kind: 'npc' | 'enemy' | 'fn';
  name: string;
  instId: string | null;
  runtime: '3d' | '2d';
  map?: string; index?: number; id?: string | null;
  enemyKind?: string; field?: 'pre_dialog' | 'post_dialog'; openedBy?: string;
}

export interface DialogueCallable {
  owner: DialogueOwner;
  /** "map_defs.py:<first line of the callable>" */
  source: string;
  helpers: string[];
  /** "get:<flag>" | "req:<flag>" in first-query order */
  reads: string[];
  branches: DialogueBranch[];
  distinctOutcomes: number;
}

export interface DialogueScene {
  owner: string | null;
  lines: DialogueLine[];
  source: string;
  map?: string; flag?: FlagName; note?: string;
}

export interface CodeBranch {
  file: string; line: number; function: string | null;
  kind: 'map' | 'npc_name' | 'npc_id' | ''; subject: string;
  op: 'Eq' | 'NotEq' | 'In' | 'NotIn'; values: string[];
}

export interface SourceSlice { line: number; endLine: number; source: string }

export interface DialogEndTable {
  questTurnins: Record<string, Stage[]>;
  questTurninsRule: string;
  shopNames: string[]; innNames: string[];
  dialogFlags3d: Record<string, FlagName>; dialogFlags3dSource: string;
  mapEnterFlags3d: Record<string, FlagName>; mapEnterFlags3dSource: string;
  shrineSeal3d: Record<string, string>; shrineSeal3dSource: string;
  onDialogEnd: { file: string; function: string; line: number; endLine: number; steps: SourceSlice[] };
  talkToNpc3d: { file: string; function: string; line: number; endLine: number; source: string };
}

export interface DialogueFile extends Envelope {
  lineFormat: Record<string, string>;
  conditionFormat: string;
  callables: Record<DialogueRef, DialogueCallable>;
  scenes: Record<string, DialogueScene>;
  dialogEnd: DialogEndTable;
  codeBranches: CodeBranch[];
  stats: {
    callables: number; npcCallables: number; enemyCallables: number;
    paths: number; npcPaths: number; npcDistinctOutcomes: number; distinctOutcomes: number;
    distinctTexts: number; npcDistinctTexts: number; npcsWithoutBranching: number;
    flagsRead: string[]; runtimeCCallsSeen: string[];
  };
}

/** dialogue_overrides.json, verbatim from the source tree. */
export interface DialogueOverride { text?: string; mood?: string; expression?: string; vo_ok?: boolean; vo_auto?: boolean }
export type DialogueOverridesFile = Record<string, DialogueOverride> & {
  __editor__?: { deleted?: string[]; added?: Record<string, string[]> };
};

// ───────────────────────────────────────────────────────────── voice.json

export interface VoiceRecord {
  key: string | null;
  voiceId: string | null;
  /** displayed text (dialogue_overrides.text_for applied) */
  text: string;
  spoken?: string;
  /** md5 hex; null = silent (no speaker key or empty text) */
  clip: string | null;
  /** clip file exists in assets/voice */
  file: boolean;
  deleted?: boolean;
  beat?: string;
}

export interface VoiceFile extends Envelope {
  model: string; defaultVoice: string; hash: string; resolution: string;
  voiceMap: Record<string, string>;
  nameToKey: Record<string, string>;
  voiceSettings: Record<string, Record<string, number | boolean>>;
  defaultSettings: Record<string, number | boolean>;
  npcOverrides: Record<string, { voice?: string; sprite?: string; name?: string; portrait?: string }>;
  speakerVoiceIds: Record<string, string>;
  /** ref -> per-branch -> per-line (parallel to dialogue.json) */
  callables: Record<DialogueRef, VoiceRecord[][]>;
  /** scene id -> per-line (dialogue.json scenes, story_text.json as "text.*", trivia) */
  scenes: Record<string, VoiceRecord[]>;
  /** "<map>/<interactable index>/<stage index>" -> per-line, parallel to maps.json */
  stages: Record<string, VoiceRecord[]>;
  recordFormat: string;
  summary: {
    records: number; voicedRecords: number; silentRecords: number;
    distinctClips: number; distinctClipsWithFile: number; distinctClipsMissing: number;
    voiceFilesOnDisk: number; missing: Array<[string, string]>;
  };
}

// ───────────────────────────────────────────────────────────── story.json

export interface FlagRecord {
  default: boolean; isDefaultKey: boolean; set: string[]; read: string[]; clear?: string[];
}

export interface StoryFile extends Envelope {
  defaults: Record<FlagName, boolean>;
  semantics: Record<string, string>;
  functions: Record<string, string[]>;
  flags: Record<FlagName, FlagRecord>;
  families: Array<{ pattern: string; members: FlagName[]; setAt: string }>;
  fstringSites: Array<{ pattern: string; how: 'set' | 'read' | 'clear'; at: string }>;
  warnings: { readNeverSet: FlagName[]; setNeverRead: FlagName[] };
  count: number;
}

// ───────────────────────────────────────────────────────────── quests / chapters

export interface FnSource { name: string; module: string; file: string; line: number; source?: string }

export interface Quest {
  id: string; title: string; kind: 'Main' | 'Side'; giver: string;
  start: FlagName[]; complete: FlagName; summary: string;
  steps: Array<[FlagName, string]>; reward: string;
}

export interface QuestsFile extends Envelope {
  quests: Quest[];
  questFormat: string;
  turnins: Record<string, Stage[]>;
  sceneLinesRegistry: unknown[];
  sceneLinesNote: string;
  handPort: FnSource[];
  transforms: Record<string, number>;
}

export interface ChaptersFile extends Envelope {
  chapters: Record<string, Record<string, unknown>>;
  liveFields: string[]; liveFieldsNote: string;
  /** map -> [cleared_flag, peace_flag, toast, inciting_flag|null] */
  crisisLift: Record<string, [FlagName, FlagName, string, FlagName | null]>;
  crisisLiftFormat: string;
  functions: FnSource[];
  transforms: Record<string, number>;
}

// ───────────────────────────────────────────────────────────── profile_catalogs.json

export interface Armor {
  name: string; hp: number; def: number; sprite: string | null; desc: string;
  /** element id (as string) -> resist fraction */
  resist?: Record<string, number>;
  perk?: { kind: string; [k: string]: unknown };
}

export interface ProfileCatalogsFile extends Envelope {
  armor: Record<string, Armor>;
  fusions: Record<string, [string, string]>;
  fusionByPair: Array<{ pair: [string, string]; id: string }>;
  cardstock: Record<string, { name: string; element: number; desc: string; dmg?: number; charged?: number; hp_cost?: boolean; [k: string]: unknown }>;
  items: Record<string, { name: string; kind: string; desc: string; amount?: number; [k: string]: unknown }>;
  keyItems: Record<string, { name: string; desc: string; group?: string; [k: string]: unknown }>;
  keyItemGroups: Record<string, number>;
  itemIconOverrides: Record<string, string>;
  noteIconKey: string;
  mapNames: Record<string, string>;
  xp: {
    BASE_HP: number; MAX_COPIES: number; SLOTS: number[];
    formulas: FnSource[];
    levels: Array<{ level: number; xp_to_next: number; max_hp: number; def_mod: number; buster: number; charged: number }>;
    armorAtLevel: Record<string, { max_hp_L1: number; def_mod_L1: number; def_mod_L30: number }>;
    battleXpSource: string;
  };
  difficulty: { names: string[]; enemyHpMult: number[]; incomingMult: number[]; default: number };
  playerProfileDefaults: Record<string, unknown>;
  /** Python dict iteration order per catalog (writer sorts object keys). */
  order?: Record<string, string[]>;
  perks: { kinds: string[]; consumers: Array<{ perk: string; file: string; line: number; call: string }> };
  shops: {
    stock: Record<string, string[]>; prices: Record<string, number>; defaultPrice: number;
    defaultPriceSource: string;
    cardStock: Record<string, Array<string | [string, number]>>; cardStockFormat: string;
    armorStock: Record<string, unknown>; titles: Record<string, string>;
    innCost: Record<string, number>; cardPriceFormula: FnSource; cardPrices: Record<string, number>;
  };
  battleRewards: {
    DROP_TABLE: Record<string, { common: string[]; rare: string[] }>;
    DROP_CARD_CHANCE: number; DROP_RARE_CHANCE: number; PRESENT_BOX_CHANCE: number;
    BOSS_ENCOUNTERS: string[];
  };
  transforms: Record<string, number>;
}

// ───────────────────────────────────────────────────────────── cards.json

export interface ChipData {
  name: string; code: string; damage: number; element: number; chip_class: 'S' | 'M' | 'G';
  mb: number; description: string; rarity: number; heals: number; effect_key: string;
  row: number;
}

export interface CardInfo {
  row: number | null; family: string; resolver: FnSource | null;
  codeRefs: string[]; summonCall: string | null; limitBreak?: boolean;
  codeOnly: boolean; nameKeyedOutsideResolver: boolean;
}

export interface CardsFile extends Envelope {
  chipFields: string[];
  chipDb: ChipData[];
  cards: Record<string, CardInfo>;
  limitBreakChips: Record<string, Omit<ChipData, 'row'>>;
  effects: Record<string, FnSource>;
  tables: Record<string, unknown>;
  renderData: Record<string, unknown>;
  rules: FnSource[];
  transforms: Record<string, number>;
}

// ───────────────────────────────────────────────────────────── small files

export interface ConstantsFile extends Envelope {
  source: string;
  values: Record<string, unknown>;
  functions: FnSource[];
  transforms: Record<string, number>;
}

export interface WarpRow {
  pads?: [Tile, Tile]; tile?: Tile;
  flags?: FlagName[]; flagsMode?: 'all';
  lock?: { kind: 'toast' | 'oden'; text: string };
  cfg?: { flag: FlagName | null; [k: string]: unknown };
}

export interface EncounterCfg { steps: [number, number]; safe: number; table: string[] }

export interface EncountersFile extends Envelope {
  randomEncounters: {
    base: Record<string, EncounterCfg>; until: Record<string, FlagName>;
    pre: Record<string, EncounterCfg>; rule: string; briarRoadNote: string;
  };
  HAZARD_ZONE_ENCOUNTER_MULT: number; ENCOUNTER_DIST: number; EXIT_DIST: number; TALK_DIST: number;
  battleBg: Record<string, string>; battleBgDefault: string;
  mapMusic: Record<string, string | null>; defaultMusic: string;
  mapMusicNight: Record<string, string>; daynightMaps: string[];
  buskers: Record<string, [number, number, string, number]>;
  battleMusic: { function: FnSource; resolved: Record<string, string> };
  mapTitle: Record<string, string>;
  mapWeather: Record<string, string>;
  interiorMaps: string[]; scrollInteriorMaps: string[];
  scrollInteriorView: Record<string, Record<string, unknown>>;
  scrollViewDefault: Record<string, unknown>;
  gatedWarps: Record<string, WarpRow[]>;
  onewayWarps: Record<string, WarpRow[]>;
  elevatorWarps: Record<string, WarpRow[]>;
  warpPairTolerance: { function: FnSource };
  MAG_GATE_LINE: string;
  bossEntryFlags: Record<string, { flag: FlagName; boss: string }>;
  mapEntry: Record<string, [FlagName, string]>;
  perMap: Record<string, {
    title: string | null; music: string | null; musicResolved: string | null;
    musicNight: string | null; battleBg: string; battleBgExplicit: boolean;
    weather: string | null; interior: boolean; scroll: boolean;
    ambience2dDead: string | null; mapEntry: [FlagName, string] | null;
  }>;
  staleTablesNotExported: string[];
  transforms: Record<string, number>;
}

export interface TriviaQuestion { q: string; options: string[]; answer: number; source: string }

export interface TriviaFile extends Envelope {
  values: {
    BANKS: Record<string, TriviaQuestion[]>;
    PATTER: Record<string, string[]>; PATTER_ODEN: Record<string, string>;
    PATTER_BY_MAP: Record<string, Record<string, string[]>>;
    PATTER_ODEN_BY_MAP: Record<string, Record<string, string>>;
    ALL_CORRECT: string[]; ALL_WRONG: string[];
    SPEAKER: string; SPEAKER_ODEN: string;
    [k: string]: unknown;
  };
  /** [speaker, key, text] (trivia_data.catalog_lines) */
  catalogLines: Array<[string, string, string]>;
  functions: FnSource[];
  prizeLogic: { file: string; line: number; source: string };
  transforms: Record<string, number>;
}

export interface FishingFile extends Envelope {
  values: {
    FISH: Array<{ id: string; item: string | null; rarity: string; weight: number; kind: string; [k: string]: unknown }>;
    _TUNE: Record<string, [number, number, number]>;
    _WAIT_RANGE: [number, number]; _WATER: [number, number, number, number];
    [k: string]: unknown;
  };
  tuneFormat: string;
  scene: FnSource;
  transforms: Record<string, number>;
}

export interface SettingsDefaultsFile extends Envelope {
  defaults: {
    music_volume: number; sfx_volume: number; vocals_volume: number; render_mode: string;
    oden_sprite: string; dof: boolean; dof_halfres: boolean; fps_cap: number; fxaa: boolean;
    msaa: boolean; difficulty: number; fullscreen: boolean;
  };
  fullscreenRule: string; platformDefaults: FnSource; load: FnSource; file: string;
}

export type OdeLine = [string, string, string];

export interface StoryTextFile extends Envelope {
  intro: { narrator: string; beats: Array<{ panel: string; text: string }> };
  edricLore: { narrator: string; beats: Array<{ panel: string; text: string }> };
  storyIntroTiming: Record<string, number>;
  storyIntroTimingNote: string;
  ode: {
    JOIN_SCENE: OdeLine[]; WARM_CARD_SCENE: OdeLine[]; FUSE_TUT_SCENE: OdeLine[];
    EVENTS: Array<{
      id: string; map: string | null; on: 'enter' | 'near'; tile?: Tile; r?: number;
      delay?: number; require: FlagName[]; flag: FlagName; lines: OdeLine[];
    }>;
    lineFormat: string;
  };
  firstBattle: {
    NARRATOR: string; ODEN: string; SLIME: string;
    PACKAGE_BEATS: Array<[string, string]>; ODEN_TUTORIAL: string[];
    STORM_TIPS: Record<string, string>; MINE_TIPS: Record<string, string>;
    MISDEAL_QUIPS: Record<string, string[]>;
  };
  bossBattle: {
    BOSS_VOICE: Record<string, string>;
    BOSS_META: Record<string, { name: string; sprite: string }>;
    TRIGGER_LABEL: Record<string, string>;
    BOSS_QUIPS: Record<string, Record<string, string[]>>;
  };
  transforms: Record<string, number>;
}

/** Every file the exporter writes, by data key (see load.ts). */
export interface DataFiles {
  maps: MapsFile;
  mapDefsRaw: MapDefsRawFile;
  mergeReport: MergeReportFile;
  dialogue: DialogueFile;
  dialogueOverrides: DialogueOverridesFile;
  voice: VoiceFile;
  story: StoryFile;
  quests: QuestsFile;
  chapters: ChaptersFile;
  profileCatalogs: ProfileCatalogsFile;
  cards: CardsFile;
  constants: ConstantsFile;
  encounters: EncountersFile;
  trivia: TriviaFile;
  fishing: FishingFile;
  settingsDefaults: SettingsDefaultsFile;
  storyText: StoryTextFile;
  /** map_overrides.json verbatim (the map editor's per-map tweaks; map_overrides.load()). */
  mapOverrides: Record<string, any>;
  /** patches.json: hand-authored asserted edits + behaviour flags (src/data/patches.ts). */
  patches: import('./patches').PatchesFile;
}

export const DATA_FILES: { readonly [K in keyof DataFiles]: string } = {
  maps: 'maps.json',
  mapDefsRaw: 'map_defs_raw.json',
  mergeReport: 'merge_report.json',
  dialogue: 'dialogue.json',
  dialogueOverrides: 'dialogue_overrides.json',
  voice: 'voice.json',
  story: 'story.json',
  quests: 'quests.json',
  chapters: 'chapters.json',
  profileCatalogs: 'profile_catalogs.json',
  cards: 'cards.json',
  constants: 'constants.json',
  encounters: 'encounters.json',
  trivia: 'trivia.json',
  fishing: 'fishing.json',
  settingsDefaults: 'settings_defaults.json',
  storyText: 'story_text.json',
  mapOverrides: 'map_overrides.json',
  patches: 'patches.json',
};
